import { AlertContainer, Copy } from 'ui/atoms';

import { PostcodeGeoJsonUrl } from '../types';

interface Props {
  countryName: string;
  totalIncidents: number;
  totalPostcodes: number;
  unmatchedCount: number;
  activeRegion: string | null;
  postcodeGeoJsonUrl: PostcodeGeoJsonUrl;
  usePolygons: boolean;
  polygonsLoading: boolean;
  polygonsError: boolean;
  hasPolygonsForActive: boolean;
}

/**
 * Compact info card displayed in the top-right corner of the map when a
 * specific region is selected. Shows country totals + status of postcode
 * polygon loading for the active region.
 *
 * When no region is selected, the parent renders RegionsSidebar instead.
 */
export function CountryInfoCard({
  countryName,
  totalIncidents,
  totalPostcodes,
  unmatchedCount,
  activeRegion,
  postcodeGeoJsonUrl,
  usePolygons,
  polygonsLoading,
  polygonsError,
  hasPolygonsForActive,
}: Props) {
  return (
    <AlertContainer
      color="white"
      className="absolute top-3 right-3 z-10 py-2 px-3.5 rounded-lg shadow-ui-kit-base flex flex-col gap-0.5 min-w-[160px]"
    >
      <div className="text-[11px] text-v2blueGray-500 uppercase tracking-[0.06em]">
        {countryName}
      </div>
      <Copy as="div" type="copy1" weight="semibold" className="text-v2blueGray-900" noColor>
        {totalIncidents.toLocaleString()} incidents · {totalPostcodes} postcodes
      </Copy>
      {unmatchedCount > 0 && (
        <div className="text-[10px] text-v2blueGray-600">
          {unmatchedCount} postcode{unmatchedCount > 1 ? 's' : ''} not matched
        </div>
      )}
      {activeRegion && postcodeGeoJsonUrl && (
        <div
          className={`text-[10px] mt-0.5 ${usePolygons ? 'text-primary' : 'text-v2blueGray-500'}`}
        >
          {usePolygons
            ? '● Showing postcode areas'
            : polygonsLoading
            ? '◌ Loading postcode areas…'
            : polygonsError
            ? '○ Failed to load postcode areas'
            : hasPolygonsForActive
            ? '○ Postcode areas unavailable'
            : '○ No postcode data for this region'}
        </div>
      )}
    </AlertContainer>
  );
}
